// @ts-strict-ignore
import { getIncludes } from '../redux/contentful-selectors'
import { FsStore } from '../redux/helpers/create-app-store'
import { Contentful } from '../types/contentful'
import uuidv4 from '../utils/guid'
import { FsCore } from '../types/fs-core'
import { setFilterGroup, useFilterGroup } from '../redux/filters/filters-actions'
import { getFilterGroup } from '../redux/filters/filters-selectors'

export function getFiltersId(state: FsCore.State, schema: Contentful.ProductTableSchemaComponent) {
  if (schema.fields.schema.filtersId) {
    return schema.fields.schema.filtersId
  } else if (typeof schema.fields.schema.filters === 'string') {
    return schema.fields.schema.filters
  } else {
    const id = uuidv4()
    schema.fields.schema.filtersId = id
    return id
  }
}
export function findFiltersSchema(
  state: FsCore.State,
  schema: Contentful.ProductTableSchemaComponent,
): Contentful.FilterSchema[] | undefined {
  let filtersSchema = schema.fields.schema.filters
  if (typeof filtersSchema === 'string') {
    filtersSchema = findLinkedFiltersSchema(state, filtersSchema)
  }
  return filtersSchema
}

export function findOrGenerateFilters(
  store: FsStore,
  productTableId: string,
  schema: Contentful.ProductTableSchemaComponent,
): FsCore.FilterGroupState | undefined {
  const filtersSchema = schema.fields.schema.filters
  let filters: FsCore.FilterGroupState | undefined
  const linkedId = schema.fields.schema.filtersId
  if (linkedId) {
    // Try to get it if it's already been created
    filters = attemptLinkToExistingFilter(store, productTableId, linkedId)
  } else if (typeof filtersSchema === 'string') {
    // This is a linked filter, the value of the schema is just the id of the filters to use
    filters = attemptLinkToExistingFilter(store, productTableId, filtersSchema)
    if (!filters) {
      filters = setupLinkedFilters(store, filtersSchema, productTableId, schema)
    }
  } else if (typeof filtersSchema === 'object') {
    // Generate the filter from schema and give it our pre-generated uuid
    filters = createFilters(
      store,
      filtersSchema,
      productTableId,
      schema,
      schema.fields.schema.filtersId,
    )
  }
  // Set the filter schema value in the product table schema object to replace potential string value
  schema.fields.schema.filters = findFiltersSchema(store.getState(), schema)
  return filters
}

function createFilters(
  store: FsStore,
  filtersSchema: Contentful.FilterSchema[],
  productTableId: string,
  tableSchema: Contentful.ProductTableSchemaComponent,
  id?: string,
): FsCore.FilterGroupState {
  if (!id) {
    id = uuidv4()
  }
  const filters = filtersSchema.map((filter) => ({
    label: filter.label,
    type: filter.type,
    name: filter.label.replace(/\s+/g, ''),
    options: filter.options.map((option) => ({
      label: option.label,
      value: option.label,
      name: option.label.replace(/\s+/g, ''),
    })),
  })) as FsCore.FilterState[]
  const filterGroup = {
    id,
    filters,
    usedBy: [productTableId],
  }

  store.dispatch(
    setFilterGroup({
      id,
      filterGroup,
    }),
  )
  // Set the id back in the schema in case we need to access it again
  tableSchema.fields.schema.filtersId = id
  return filterGroup
}

function attemptLinkToExistingFilter(
  store: FsStore,
  productTableId: string,
  filterGroupId: string,
): FsCore.FilterGroupState | undefined {
  const existingFilter = getFilterGroup(store.getState(), filterGroupId)
  if (existingFilter) {
    store.dispatch(
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useFilterGroup({
        filterGroupId: filterGroupId,
        usedById: productTableId,
      }),
    )
  }
  return getFilterGroup(store.getState(), filterGroupId)
}

function setupLinkedFilters(
  store: FsStore,
  filtersId: string,
  productTableId: string,
  tableSchema: Contentful.ProductTableSchemaComponent,
): FsCore.FilterGroupState | undefined {
  const filtersSchema = findLinkedFiltersSchema(store.getState(), filtersId)
  if (Array.isArray(filtersSchema)) {
    // If this isn't true then someone got the reference wrong, but we don't have any good way of doing error handling
    const filters = createFilters(store, filtersSchema, productTableId, tableSchema, filtersId)
    return filters
  }
  return undefined
}

function findLinkedFiltersSchema(
  state: FsCore.State,
  filtersId: string,
): Contentful.FilterSchema[] | undefined {
  const entity: Contentful.Module<Contentful.FilterSchema[] | undefined> =
    getIncludes(state)[filtersId]
  const filtersSchema = entity.fields.data
  return filtersSchema
}
